import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Download, Plus, FileText, FileSpreadsheet, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';
import { usePermission } from '@/components/guards/permission-guard';
import { dateStamp } from '@/lib/download-xlsx';
import { downloadMovementsPdf, downloadMovementsXlsx } from '@/lib/cari-movements-export';
import { TRADING_COMPANY } from '@/lib/report-company';
import type { MovementDoc } from '@/lib/cari-movements-doc';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { AddMovementDialog } from '@/components/trading/cari/add-movement-dialog';

const selectClass =
  'h-8 border border-border bg-background px-2.5 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20';

// Fallback while the registry loads; the live list comes from tradingCariType.
const FALLBACK_CARI_TYPES = ['M', 'T', 'S', 'B', 'ORT', 'P', 'AH', 'E', 'X', 'I'];
const CURRENCIES = ['TRY', 'EUR', 'GBP', 'USD'] as const;
const CURRENCY_NAMES: Record<string, string> = {
  TRY: 'TÜRK LİRASI - TL',
  EUR: 'EURO - EUR',
  GBP: 'İNGİLİZ STERLİNİ - GBP',
  USD: 'AMERİKAN DOLARI - USD',
};

export function CariTransactionsPage() {
  const { t, i18n } = useTranslation('trading');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const canCreate = usePermission('CUSTOMERS', 'CREATE');

  const [addOpen, setAddOpen] = useState(false);
  const [debounced, setDebounced] = useSessionState(
    'trading:cari-transactions:debouncedSearch',
    ''
  );
  const [search, setSearch] = useState(debounced);
  const [cariType, setCariType] = useSessionState('trading:cari-transactions:cariType', '');
  const [currency, setCurrency] = useSessionState('trading:cari-transactions:currency', '');
  const [dateFrom, setDateFrom] = useSessionState('trading:cari-transactions:dateFrom', '');
  const [dateTo, setDateTo] = useSessionState('trading:cari-transactions:dateTo', '');
  const [sourceKind, setSourceKind] = useSessionState('trading:cari-transactions:sourceKind', '');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const utils = trpc.useUtils();

  const { data: cariTypeRows } = trpc.tradingCariType.list.useQuery();
  const cariTypeCodes = cariTypeRows?.map((tp) => tp.code) ?? FALLBACK_CARI_TYPES;

  const onSearch = (v: string) => {
    setSearch(v);
    setTimeout(() => {
      setDebounced(v);
      setPage(1);
    }, 300);
  };

  const filters = {
    search: debounced || undefined,
    cariType: cariType || undefined,
    currency: (currency || undefined) as (typeof CURRENCIES)[number] | undefined,
    dateFrom: dateFrom ? new Date(dateFrom) : undefined,
    dateTo: dateTo ? new Date(dateTo) : undefined,
    sourceKind: (sourceKind || undefined) as 'imported' | 'manual' | 'trade' | undefined,
  };

  const buildDoc = (
    res: Awaited<ReturnType<typeof utils.tradingCari.exportAllMovements.fetch>>
  ): MovementDoc => {
    const fmtDay = (val: string) => new Date(val).toLocaleDateString(dateLocale);
    const periodParts = [
      dateFrom || dateTo
        ? `${dateFrom ? fmtDay(dateFrom) : '…'} – ${dateTo ? fmtDay(dateTo) : '…'}`
        : '',
    ].filter(Boolean);
    return {
      company: TRADING_COMPANY,
      meta: {
        mode: 'list',
        title: t('cariTransactions.title', 'Account Movements').toLocaleUpperCase(
          i18n.language === 'tr' ? 'tr-TR' : 'en-US'
        ),
        periodLabel: periodParts.join('  ·  ') || t('cariTransactions.allTime', 'All dates'),
        generatedLabel: t('cariTransactions.exportGenerated', 'Generated {{ts}}', {
          ts: new Date().toLocaleString(dateLocale),
        }),
        params: [
          {
            label: t('cariTransactions.currency', 'Currency'),
            value: currency || t('cariTransactions.all', 'All'),
          },
          {
            label: t('cariTransactions.type', 'Type'),
            value: cariType || t('cariTransactions.all', 'All'),
          },
          ...(sourceKind
            ? [
                {
                  label: t('cariTransactions.source', 'Source'),
                  value:
                    sourceKind === 'manual'
                      ? t('cariTransactions.manual', 'Manual')
                      : sourceKind === 'trade'
                        ? t('cariTransactions.trade', 'Trade')
                        : t('cariTransactions.imported', 'Imported'),
                },
              ]
            : []),
          ...(debounced
            ? [{ label: t('cariTransactions.search', 'Search'), value: debounced }]
            : []),
        ],
        columns: {
          no: '#',
          date: t('cariTransactions.date', 'Date'),
          cari: t('cariTransactions.cari', 'Cari'),
          type: t('cariTransactions.type', 'Type'),
          code: t('cariTransactions.code', 'Code'),
          voucher: t('cariTransactions.voucherNo', 'Voucher No'),
          description: t('cariTransactions.description', 'Description'),
          currency: t('cariTransactions.currency', 'Currency'),
          fxDebit: t('cariTransactions.fxDebit', 'FX Debit'),
          fxCredit: t('cariTransactions.fxCredit', 'FX Credit'),
          tryDebit: t('cariTransactions.tryDebit', 'TL Debit'),
          tryCredit: t('cariTransactions.tryCredit', 'TL Credit'),
          balance: t('cariTransactions.balance', 'Balance'),
        },
        totalLabel: t('cariTransactions.total', 'TOTAL'),
        balanceLabel: t('cariTransactions.balance', 'BALANCE'),
        debitSide: t('cariTransactions.debitSide', '(B)'),
        creditSide: t('cariTransactions.creditSide', '(A)'),
        manualTag: 'M',
        emptyLabel: t('cariTransactions.noResults', 'No movements found'),
        pageLabel: (p, tt) =>
          t('cariTransactions.exportPage', 'Page {{page}} / {{total}}', { page: p, total: tt }),
        currencyNames: CURRENCY_NAMES,
      },
      data: { rows: res.rows, totalsByCurrency: res.totalsByCurrency },
    };
  };

  const runExport = async (kind: 'pdf' | 'xlsx') => {
    setExporting(true);
    try {
      const res = await utils.tradingCari.exportAllMovements.fetch(filters);
      const doc = buildDoc(res);
      const base = `trading-cari-hareketleri-${dateStamp()}`;
      if (kind === 'pdf') await downloadMovementsPdf(doc, `${base}.pdf`);
      else await downloadMovementsXlsx(doc, `${base}.xlsx`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading } = trpc.tradingCari.allMovements.useQuery({ ...filters, page });

  const fmt = (n: number) =>
    new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      n
    );

  const items = data?.items ?? [];
  const pag = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('cariTransactions.title', 'Cari Movements')}</h1>
          <p className="text-muted-foreground">
            {t('cariTransactions.subtitle', 'All current-account movements across every cari')}
            {pag ? ` (${pag.total})` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/trading/cari/bank-matching')}>
            {t('bankMatching.title', 'Banka Cari Eşleştirme')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting || !items.length}>
                {exporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                {t('cariTransactions.export', 'Export')}
                <ChevronDown className="ml-2 h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => runExport('pdf')}>
                <FileText className="h-4 w-4 text-red-600" />
                {t('cariTransactions.exportPdf', 'PDF')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => runExport('xlsx')}>
                <FileSpreadsheet className="h-4 w-4 text-green-600" />
                {t('cariTransactions.exportExcel', 'Excel')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canCreate && (
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('cariTransactions.addMovement', 'Add Movement')}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label className="text-xs">{t('cariTransactions.search', 'Search')}</Label>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t(
              'cariTransactions.searchPlaceholder',
              'Name, description, voucher, code...'
            )}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 mt-1 h-8 w-72 border px-3 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cariTransactions.type', 'Type')}</Label>
          <select
            value={cariType}
            onChange={(e) => {
              setCariType(e.target.value);
              setPage(1);
            }}
            className={`${selectClass} block w-28`}
          >
            <option value="">{t('cariTransactions.allTypes', 'All')}</option>
            {cariTypeCodes.map((ct) => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cariTransactions.currency', 'Currency')}</Label>
          <select
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
              setPage(1);
            }}
            className={`${selectClass} block w-24`}
          >
            <option value="">{t('cariTransactions.all', 'All')}</option>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cariTransactions.dateFrom', 'From')}</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="w-36"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cariTransactions.dateTo', 'To')}</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="w-36"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cariTransactions.source', 'Source')}</Label>
          <select
            value={sourceKind}
            onChange={(e) => {
              setSourceKind(e.target.value);
              setPage(1);
            }}
            className={`${selectClass} block w-32`}
          >
            <option value="">{t('cariTransactions.all', 'All')}</option>
            <option value="imported">{t('cariTransactions.imported', 'Imported')}</option>
            <option value="manual">{t('cariTransactions.manual', 'Manual')}</option>
            <option value="trade">{t('cariTransactions.trade', 'Trade')}</option>
          </select>
        </div>
        {(search || cariType || currency || dateFrom || dateTo || sourceKind) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch('');
              setDebounced('');
              setCariType('');
              setCurrency('');
              setDateFrom('');
              setDateTo('');
              setSourceKind('');
              setPage(1);
            }}
          >
            {t('cariTransactions.clear', 'Clear')}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="px-3 py-3 text-left font-medium">
                {t('cariTransactions.date', 'Tarih')}
              </th>
              <th className="px-3 py-3 text-left font-medium">
                {t('cariTransactions.cari', 'Cari')}
              </th>
              <th className="px-3 py-3 text-left font-medium">
                {t('cariTransactions.voucherNo', 'Fiş No')}
              </th>
              <th className="px-3 py-3 text-left font-medium">
                {t('cariTransactions.description', 'Açıklama')}
              </th>
              <th className="px-3 py-3 text-center font-medium">
                {t('cariTransactions.currency', 'Döviz')}
              </th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cariTransactions.fxDebit', 'Döviz Borç')}
              </th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cariTransactions.fxCredit', 'Döviz Alacak')}
              </th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cariTransactions.tryDebit', 'TL Borç')}
              </th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cariTransactions.tryCredit', 'TL Alacak')}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={9} className="py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="text-muted-foreground py-8 text-center">
                  {t('cariTransactions.noResults', 'No movements found')}
                </td>
              </tr>
            )}
            {items.map((m) => (
              <tr
                key={m.id}
                className="hover:bg-muted/30 cursor-pointer border-b last:border-0"
                onClick={() =>
                  m.entityId && navigate(`/trading/cari/${m.entitySource}/${m.entityId}/movements`)
                }
              >
                <td className="whitespace-nowrap px-3 py-2.5">
                  {new Date(m.date).toLocaleDateString('tr-TR')}
                </td>
                <td className="px-3 py-2.5">
                  <span className="font-medium">{m.counterpartyName}</span>
                  <span className="bg-muted text-muted-foreground ml-1.5 rounded px-1.5 py-0.5 text-[10px]">
                    {m.cariType}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs">
                  {m.voucherNo}
                  {m.isManual && (
                    <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
                      M
                    </span>
                  )}
                  {m.isTrade && (
                    <span
                      className="ml-1.5 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-700"
                      title={t('cariTransactions.trade', 'Trade')}
                    >
                      T
                    </span>
                  )}
                </td>
                <td className="max-w-[280px] px-3 py-2.5">
                  <span className="block truncate">{m.description}</span>
                </td>
                <td className="px-3 py-2.5 text-center">{m.currency}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.fxDebit > 0 ? fmt(m.fxDebit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.fxCredit > 0 ? fmt(m.fxCredit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.tryDebit > 0 ? fmt(m.tryDebit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.tryCredit > 0 ? fmt(m.tryCredit) : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pag && pag.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">
            {(page - 1) * pag.pageSize + 1}-{Math.min(page * pag.pageSize, pag.total)} / {pag.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t('cariTransactions.previous', 'Previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pag.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('cariTransactions.next', 'Next')}
            </Button>
          </div>
        </div>
      )}

      <AddMovementDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
